// Общая логика api-client для admin/ и frontend/ (ранее дублировалась построково).
// Единая публичная точка — фабрика createApiClient({ storageKey, onUnauthorized }):
// тонкая обёртка над fetch (базовый URL, Authorization, единая обработка 401/403).
// Специфика приложения (ключ sessionStorage, обработчик unauthorized) передаётся
// параметрами, а не хардкодится.

// PARA-131 (AD-1): третий необязательный аргумент — разобранное тело ошибки.
// Аддитивно: message остаётся СЫРЫМ текстом ответа, поэтому поведение уже
// написанного кода (в т.ч. admin/) не меняется. Потребителям тела (напр.
// task_limit из 402) больше не нужен JSON.parse(error.message).
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Тело ошибки как JSON; не-JSON (HTML прокси, пустая строка) → undefined. */
function parseErrorBody(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

export interface ApiRequestOptions {
  method?: string;
  body?: unknown;
  // Запросы без токена (напр. первичная авторизация) — не подставлять Authorization.
  skipAuth?: boolean;
  // PARA-132 (AD-1): opt-in отписка КОНКРЕТНОГО запроса от разлогина по 403.
  // Нужна там, где 403 — штатный бизнес-ответ, а не потеря доступа (голосовой
  // ввод: тариф ниже Ultimate и неподтверждённое согласие). Дефолт
  // (undefined) = прежнее поведение, поэтому все существующие вызовы
  // (включая admin/) продолжают разлогиниваться по 403. На 401 не влияет.
  allowForbidden?: boolean;
}

/** Тело запроса для fetch: FormData уходит как есть (PARA-132, AD-2), всё
 * остальное — прежним JSON-путём. */
function buildRequestBody(body: unknown, isMultipart: boolean): BodyInit | undefined {
  if (body === undefined) {
    return undefined;
  }
  return isMultipart ? (body as FormData) : JSON.stringify(body);
}

export interface ApiClientConfig {
  storageKey: string;
  onUnauthorized: () => void;
}

export function createApiClient({ storageKey, onUnauthorized }: ApiClientConfig) {
  const tokenStorage = {
    get(): string | null {
      return sessionStorage.getItem(storageKey);
    },
    set(token: string): void {
      sessionStorage.setItem(storageKey, token);
    },
    clear(): void {
      sessionStorage.removeItem(storageKey);
    },
  };

  function buildUrl(path: string): string {
    const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '';
    return `${baseUrl}${path}`;
  }

  async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
    // PARA-132 (AD-2): multipart — единственная ветка. Content-Type для FormData
    // выставляет браузер вместе с boundary; ручной заголовок его потеряет.
    const isMultipart = options.body instanceof FormData;
    const headers: Record<string, string> = isMultipart
      ? {}
      : { 'Content-Type': 'application/json' };
    if (!options.skipAuth) {
      const token = tokenStorage.get();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    const response = await fetch(buildUrl(path), {
      method: options.method ?? 'GET',
      headers,
      body: buildRequestBody(options.body, isMultipart),
    });

    // 401 (нет/невалидный токен) и 403 (бан уже выданной сессии) — для UI разница
    // не имеет значения, в обоих случаях нужен новый вход (non-disclosure message).
    // PARA-132 (AD-1): 403 с allowForbidden проваливается в общую ветку ошибок —
    // вызывающий получает ApiError(403) с разобранным телом и остаётся в сессии.
    if (response.status === 401 || (response.status === 403 && options.allowForbidden !== true)) {
      onUnauthorized();
      throw new ApiError(response.status, 'Unauthorized');
    }

    if (!response.ok) {
      const text = await response.text();
      throw new ApiError(response.status, text, parseErrorBody(text));
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }

  return { apiRequest, ApiError, tokenStorage };
}
